import { useEffect, useState } from 'react'
import { Check, Copy, Link2 } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { tokenManager } from '@/lib/tokenManager'

interface Props {
  open: boolean
  threadId: string
  messageId: string
  partIndex: number
  partKind: string
  onClose: () => void
}

const KIND_LABEL: Record<string, string> = {
  table: 'table',
  chart: 'chart',
  artifact: 'artifact',
}

/**
 * Creates a public share link scoped to a single rendered part (table /
 * chart / artifact card) inside one assistant message. Recipients open
 * /chat/share/:token and see ONLY that part - no surrounding
 * conversation, no login required.
 */
export function SharePartDialog({ open, threadId, messageId, partIndex, partKind, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (!open) {
      setUrl(null)
      setError(null)
      setCopied(false)
      return
    }
    let cancelled = false
    const run = async () => {
      setCreating(true)
      setError(null)
      try {
        const token = tokenManager.get()
        const res = await fetch(`/api/chatbot/threads/${threadId}/shares`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ message_id: messageId, part_index: partIndex }),
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error((body as { error?: string }).error || `HTTP ${res.status}`)
        }
        const data = (await res.json()) as { share: { url: string | null } }
        if (cancelled) return
        setUrl(data.share.url ?? null)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to create share link.')
      } finally {
        if (!cancelled) setCreating(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [open, threadId, messageId, partIndex])

  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setError('Copy failed - the link is visible below.')
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (o ? null : onClose())}
      title={`Share this ${KIND_LABEL[partKind] || 'result'}`}
      description="Anyone with this link can view just this result - no Bokapsys login required. Revoke any time from the thread menu."
    >
      <div className="space-y-3">
        {creating ? (
          <div className="text-xs text-muted-foreground">Creating link…</div>
        ) : error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : url ? (
          <div className="flex items-center gap-2">
            <Link2 className="size-4 text-muted-foreground" />
            <Input
              value={url}
              readOnly
              className="h-8 font-mono text-[11px]"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button type="button" size="icon" variant="ghost" className="size-8" onClick={copy} aria-label="Copy link">
              {copied ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
